import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useInputCommon } from '../../hooks';
import styles from './Input.module.scss';

type InputProps = {
    label?: string;
    error?: boolean;
    errorText?: string;
    helperText?: string;
    type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'search' | 'date' | 'datetime-local' | 'time';
    showPasswordToggle?: boolean;
} & React.InputHTMLAttributes<HTMLInputElement>;

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
    (
        { label, error, errorText, helperText, className, type = 'text', showPasswordToggle = false, ...inputProps },
        ref
    ) => {
        const [showPassword, setShowPassword] = useState(false);

        const inputClassName =
            `${styles.input} ${error ? styles.error : ''} ${showPasswordToggle ? styles.hasToggle : ''} ${className || ''}`.trim();
        const actualType = type === 'password' && showPasswordToggle && showPassword ? 'text' : type;

        const { labelElement, errorTextElement, helperTextElement, containerClasses } = useInputCommon({
            id: inputProps.id,
            label,
            error: !!error,
            errorText,
            helperText,
            required: !!inputProps.required,
            disabled: !!inputProps.disabled,
            className,
            inputType: 'input',
        });

        return (
            <div className={containerClasses}>
                {labelElement}
                <div className={styles.inputWrapper}>
                    <input type={actualType} className={inputClassName} ref={ref} {...inputProps} />
                    {type === 'password' && showPasswordToggle && (
                        <button
                            type='button'
                            className={styles.toggleButton}
                            onClick={() => setShowPassword(!showPassword)}
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                    )}
                </div>
                {errorTextElement}
                {helperTextElement}
            </div>
        );
    }
);

Input.displayName = 'Input';
